import { useState } from 'react';
import StyledButton, { StyledButtonProps } from './StyledButton';

interface StyledButtonLoaderProps extends StyledButtonProps {
  successTimeout?: number;
  hasSucceeded?: boolean;
  successTitle: string;
  errorTitle?: string;
  onClick: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => unknown;
}

const isPromiseLike = (value: unknown): value is PromiseLike<unknown> =>
  typeof value === 'object' &&
  value !== null &&
  'then' in value &&
  typeof (value as PromiseLike<unknown>).then === 'function';

const StyledButtonLoader = ({
  successTimeout = 2000,
  hasSucceeded = true,
  successTitle,
  errorTitle,
  onClick,
  ...props
}: StyledButtonLoaderProps) => {
  const [feedback, setFeedback] = useState<'success' | 'error' | null>(null);
  const [isPending, setIsPending] = useState(false);

  const { title, disabled, ...otherProps } = props;

  const modifiedOnClick = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    if (isPending || feedback) return;

    setIsPending(true);
    let nextFeedback: 'success' | 'error' | null = null;
    try {
      const result = onClick(event);
      if (isPromiseLike(result)) {
        await result;
      }
      nextFeedback = hasSucceeded ? 'success' : 'error';
    } catch (error) {
      const isValidationFailure =
        error instanceof Error && error.message === 'validation_failed';
      nextFeedback = isValidationFailure && errorTitle ? 'error' : null;
    } finally {
      setIsPending(false);
      setFeedback(nextFeedback);
      if (nextFeedback) {
        setTimeout(() => {
          setFeedback(null);
        }, successTimeout);
      }
    }
  };

  return (
    <StyledButton
      title={
        feedback === 'success'
          ? successTitle
          : feedback === 'error'
            ? (errorTitle ?? title)
            : title
      }
      onClick={modifiedOnClick}
      {...otherProps}
      disabled={disabled || isPending || feedback !== null}
    />
  );
};

export default StyledButtonLoader;
